import { useEffect, useState } from 'react';
import { Timer, Shield, Recycle, DollarSign, CheckCircle, Leaf, Play } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const Hero = () => {
  const [heroTitle, setHeroTitle] = useState<string>("Excess Market");
  const [heroSubtitle, setHeroSubtitle] = useState<string>("אקסס מרקט - עודפי חומרי בנייה, עיצוב ולייף סטייל מהספקים המובילים.");
  const [videoUrl, setVideoUrl] = useState<string>("https://voyrsahkomcidcwjxcst.supabase.co/storage/v1/object/public/uploads/excess%20vid.mp4");

  useEffect(() => {
    // Non-blocking async load after initial render
    const load = async () => {
      try {
        const { data, error } = await (supabase as any)
          .from('page_contents')
          .select('section_name, content_type, content')
          .eq('page_name', 'homepage')
          .in('section_name', ['hero_title', 'hero_subtitle', 'explainer_video_url']);
        if (error) throw error;
        data?.forEach((row: any) => {
          if (row.section_name === 'hero_title') setHeroTitle(row.content || heroTitle);
          if (row.section_name === 'hero_subtitle') setHeroSubtitle(row.content || heroSubtitle);
          if (row.section_name === 'explainer_video_url') setVideoUrl(row.content || videoUrl);
        });
      } catch (e) {
        console.error('Failed to load hero content', e);
      }
    };
    // Defer load to allow initial render
    setTimeout(load, 0);
  }, []);

  const features = [{
    icon: DollarSign,
    title: "מחירים משתלמים",
    description: "בזכות חוזים בלעדיים עם ספקים ויבואנים, תוכלו ליהנות מהנחות משמעותיות ולרכוש מוצרים איכותיים בעלות נמוכה"
  }, {
    icon: CheckCircle,
    title: "איכות ובטיחות",
    description: "כל מוצר עובר בדיקה, מדידה וסימון כדי להבטיח איכות ועמידה בתקנים. אנחנו מספקים תמונות ומידע מפורט"
  }, {
    icon: Leaf,
    title: "תרומה לסביבה",
    description: "קנייה של חומרי‑בנייה עודפים מצמצמת את ייצור המוצרים החדשים ואת זיהום הסביבה"
  }];

  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
    {/* Background Image with Overlay */}
    <div className="absolute inset-0">
      <img
        src="/uploads/7abb424c-448f-4489-8b45-04aa70154302.png"
        alt="מחסן חומרי בנייה"
        className="w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
        sizes="100vw"
        srcSet="/uploads/7abb424c-448f-4489-8b45-04aa70154302.png 1009w"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-transparent"></div>
    </div>

    {/* Content */}
    <div className="relative z-10 container mx-auto px-4 text-center text-white">
      <div className="max-w-4xl mx-auto">
        {/* SEO H1 - Visually hidden but accessible to search engines */}
        <h1 className="sr-only">{heroTitle} - {heroSubtitle}</h1>

        {/* Logo */}
        <div className="mb-8">
          <img
            src="/uploads/126607ab-a19b-43b5-a9b4-11c3949c1325.png"
            alt="Excess Market"
            className="h-20 md:h-24 w-auto mx-auto"
            loading="eager"
            fetchPriority="high"
            sizes="(max-width: 768px) 80px, 96px"
            srcSet="/uploads/126607ab-a19b-43b5-a9b4-11c3949c1325.png 1008w"
          />
        </div>

        {/* Subtitle */}
        <p className="text-2xl md:text-3xl mb-8 text-white/90 hebrew-body max-w-3xl mx-auto">{heroSubtitle}
        </p>

        {/* Video Button & Modal */}
        <div className="flex justify-center">
          <Dialog>
            <DialogTrigger asChild>
              <Button
                size="lg"
                className="bg-lime hover:bg-lime-hover text-white rounded-full px-8 py-6 text-xl flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg group"
              >
                <div className="bg-white/20 rounded-full p-2 group-hover:bg-white/30 transition-colors">
                  <Play className="w-5 h-5 fill-current" />
                </div>
                <span>צפו בסרטון ההסבר</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl p-0 bg-black border-none ring-0 focus:ring-0 overflow-visible [&>button]:-top-4 [&>button]:-right-4 [&>button]:left-auto [&>button]:bg-white [&>button]:text-black [&>button]:rounded-full [&>button]:w-10 [&>button]:h-10 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:shadow-xl [&>button]:opacity-100 [&>button]:hover:bg-gray-100 [&>button]:transition-all [&>button]:z-[60] [&>button_svg]:w-5 [&>button_svg]:h-5">
              <DialogHeader className="sr-only">
                <DialogTitle>סרטון הסבר - Excess Market</DialogTitle>
              </DialogHeader>
              <div className="w-full">
                <AspectRatio ratio={16 / 9}>
                  {videoUrl.match(/\.(mp4|webm|ogg)$/) || videoUrl.includes('supabase.co/storage') ? (
                    <video
                      src={videoUrl}
                      controls
                      autoPlay
                      className="w-full h-full"
                    />
                  ) : (
                    <iframe
                      src={`${videoUrl}${videoUrl.includes('?') ? '&' : '?'}autoplay=1`}
                      title="Excess Market Explainer Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full border-0"
                    ></iframe>
                  )}
                </AspectRatio>
              </div>
            </DialogContent>
          </Dialog>
        </div>

      </div>
    </div>

    {/* Scroll Indicator */}
    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
      <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
        <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
      </div>
    </div>
  </section>;
};

export default Hero;
